import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { ScatterPlot } from "@mui/x-charts";
import { ChartsTooltip } from "@mui/x-charts";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import Paper from "@mui/material/Paper";
import { Box } from "@mui/material";
import Button from "@/components/button";
import CustomTooltip from "./CustomTooltip";
import useStyles from "./styles";
import { Colors } from "@/theme";
import { max, min } from "lodash";
import { getLocalisationVal } from "@/utils/localizationUtil";

const CHART_HEIGHT = 400;
const Chart = ({ products, store, localization, range }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const usefulProducts = products?.filter(
    (item) =>
      parseFloat(item?.prices?.[0]?.gross_price || "0") > 0 ||
      item.store.id === store.id
  );
  const storeProduct = usefulProducts?.find(
    (item) => item.store.id === store.id
  );
  const otherProducts = usefulProducts.filter(
    (product) => product?.store?.id !== store.id
  );

  const xMin = Math.floor(
    min(otherProducts?.map((product) => product.distance)) * 0.95
  );
  const xMax = Math.min(
    range,
    Math.ceil(max(otherProducts?.map((product) => product.distance)))
  );
  const yMin =
    min(
      usefulProducts?.map((product) =>
        parseFloat(product?.prices?.[0]?.gross_price)
      )
    ) * 0.95;
  const yMax = Math.floor(
    max(
      usefulProducts?.map((product) =>
        parseFloat(product?.prices?.[0]?.gross_price)
      )
    ) * 1.1
  );

  const data = otherProducts.map((product, id) => ({
    id,
    x: product.distance,
    y: parseFloat(
      getLocalisationVal(localization, product?.prices?.[0]?.gross_price)
    ),
    store: product.store,
    trend: product?.prices?.[0]?.history?.trend,
    last_updated: product?.prices?.[0]?.last_updated,
    history_prices: product?.prices?.[0]?.history?.points
      ?.toReversed()
      ?.map((point) =>
        parseFloat(getLocalisationVal(localization, point.gross_price))
      ),
    history_times: product?.prices?.[0]?.history?.points
      ?.toReversed()
      ?.map((point) => moment(point.time)),
  }));

  const storePrice = parseFloat(storeProduct?.prices?.[0]?.gross_price);

  const config = {
    sx: {
      "& .MuiChartsAxis-root .MuiChartsAxis-line": {
        stroke: Colors.GRAY_BORDER,
      },
      "& .MuiChartsAxis-root .MuiChartsAxis-tick": {
        stroke: Colors.GRAY_BORDER,
      },
    },
    series: [
      {
        id: "pvId",
        type: "scatter",
        data,
        yAxisKey: "yKey",
        markerSize: 5,
        color: Colors.GREEN_DARK,
      },
    ],
    height: CHART_HEIGHT,
    xAxis: [
      {
        min: xMin,
        max: xMax,
        valueFormatter: (value) => `${value} mi`,
      },
    ],
    yAxis: [
      {
        id: "yKey",
        valueFormatter: (value) => `${value}`,
        tickLabelStyle: {
          textAnchor: "middle",
          transform: "translate(-25px, -5px) rotate(-90deg)",
          fontWeight: "bold",
          fontSize: 14,
          color: Colors.GRAY_DARK,
        },
        min: yMin,
        max: yMax,
      },
    ],
  };

  return (
    <Paper
      sx={{
        borderRadius: 4,
        position: "relative",
        boxShadow: "none",
        marginBottom: 15,
        borderWidth: 1,
        borderColor: Colors.GRAY_BORDER,
        borderStyle: "solid",
      }}
    >
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingTop={5}
        paddingX={11}
      >
        <span className={classes.codeText}>
          {`${storeProduct.product_code} `}
          {getLocalisationVal(localization, storePrice)}
          <span style={{ fontSize: 14 }}>p</span>
        </span>
      </Box>
      <ResponsiveChartContainer {...config}>
        <ScatterPlot />
        <ChartsReferenceLine
          y={storePrice}
          lineStyle={{ stroke: Colors.ORANGE, strokeWidth: 2 }}
          labelStyle={{ fontSize: "14", fontWeight: "bold" }}
          label={`${getLocalisationVal(localization, storePrice)}`}
          labelAlign="start"
        />
        <ChartsXAxis />
        <ChartsYAxis />
        <ChartsTooltip slots={{ itemContent: CustomTooltip }} trigger="item" />
      </ResponsiveChartContainer>
    </Paper>
  );
};

export default Chart;
